import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Radio, Spin, Switch, message } from "antd";

import * as api from "./controller";
import { OFFICE_FEATURES, sidebarAccess } from "../sidebarAccess";
import "./leadSettings.css";

const DEFAULT_WHO = { everyone: "everyone", super_admin: "super admins only" };

/**
 * Office drawer → Features: which tools this office's users see.
 *
 * Each feature is Default / On / Off. Default keeps the product's standard
 * behaviour (the label says who that means); only On/Off are stored. Super admins
 * always see everything, so "Office users see it" is worked out with the same
 * policy the sidebar and page locks use (sidebarAccess) — never a second copy.
 */
export default function OfficeFeaturesPanel({ officeId }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(null); // feature key being saved

  const load = useCallback(async () => {
    const r = await api.getOfficeFeatures(officeId);
    const ok = r.status >= 200 && r.status < 300 && r.json && r.json.features;
    setFailed(!ok);
    setData(ok ? r.json : null);
  }, [officeId]);
  useEffect(() => { load(); }, [load]);

  const change = async (key, mode) => {
    const before = data;
    // Show the change at once; put it back if the save fails.
    setData({ ...data, features: { ...data.features, [key]: mode } });
    setSaving(key);
    const r = await api.updateOfficeFeatures(officeId, { [key]: mode });
    setSaving(null);
    if (r.status >= 200 && r.status < 300 && r.json && r.json.features) {
      setData(r.json);
    } else {
      setData(before);
      message.error((r.json && r.json.detail) || "Could not update this feature.");
    }
  };

  if (failed) {
    return (
      <p className="crm-sub">
        Couldn&rsquo;t load this office&rsquo;s features.{" "}
        <Button size="small" type="link" style={{ padding: 0 }} onClick={load}>Try again</Button>
      </p>
    );
  }
  if (!data) return <Spin />;

  // What a practice admin of this office sees — the widest non-super-admin view.
  const officeView = sidebarAccess({ isPracticeAdmin: true, isSuperAdmin: false, features: data.features });
  const banking = data.banking_configured;

  return (
    <div className="leadset-panel">
      <p className="leadset-panel-title">Features for this office</p>
      <p className="crm-sub leadset-panel-sub">
        Choose which tools this office&rsquo;s users see in their menu. A switched-off tool is
        hidden and its pages show &ldquo;not enabled for your office&rdquo;. Super admins always
        see everything. Changes reach the office&rsquo;s users within a minute.
      </p>
      {OFFICE_FEATURES.map((f) => {
        const mode = data.features[f.key] || "default";
        const visible = !!officeView[f.access];
        return (
          <div className="leadset-row" key={f.key}>
            <div className="leadset-row-label">
              {f.label}
              <div className="leadset-hint">
                Office users see it: <strong>{visible ? "Yes" : "No"}</strong>
                {f.key === "payments_ar" && visible && banking === false
                  ? " — pages stay blurred until banking is set up"
                  : ""}
              </div>
              {f.key === "targets_rewards" && mode === "on" ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 6 }}
                  message="Targets and Rewards has no backend on this server yet — the page will not load data."
                />
              ) : null}
            </div>
            <div className="leadset-row-control">
              <Radio.Group
                size="small"
                optionType="button"
                value={mode}
                disabled={saving === f.key}
                onChange={(e) => change(f.key, e.target.value)}
                options={[
                  { value: "default", label: `Default (${DEFAULT_WHO[f.byDefault]})` },
                  { value: "on", label: "On" },
                  { value: "off", label: "Off" },
                ]}
              />
            </div>
          </div>
        );
      })}
      <FtcRequestsUsage officeId={officeId} />
      <div className="leadset-row">
        <div className="leadset-row-label">
          Bank account
          <div className="leadset-hint">
            {banking === true
              ? "Configured — Payments & AR pages open normally."
              : banking === false
                ? "Not configured — Payments & AR pages are blurred for this office's users, with a note to contact support@smilepass.com."
                : "Couldn't check right now — Payments & AR pages open as usual."}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * "Requests our team starts": when our FTC team checks a patient's insurance on
 * its own (before an upcoming appointment, say), does that count toward this
 * office's usage like the office's own requests? Yes by default. The server
 * enforces it — the meters and the plan-limit checks (crm.services.ftc_requests).
 */
function FtcRequestsUsage({ officeId }) {
  const [counts, setCounts] = useState(null);   // null while loading
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.getOfficeUsageSettings(officeId).then((r) => {
      if (!alive) return;
      const ok = r.status >= 200 && r.status < 300 && r.json && typeof r.json.count_ftc_requests === "boolean";
      setFailed(!ok);
      setCounts(ok ? r.json.count_ftc_requests : null);
    });
    return () => { alive = false; };
  }, [officeId]);

  const change = async (value) => {
    const before = counts;
    setCounts(value);
    setSaving(true);
    const r = await api.updateOfficeUsageSettings(officeId, { count_ftc_requests: value });
    setSaving(false);
    if (r.status >= 200 && r.status < 300 && r.json) {
      setCounts(r.json.count_ftc_requests);
      message.success(value ? "Our team's requests count toward this office's usage." : "Our team's requests no longer count toward this office's usage.");
    } else {
      setCounts(before);
      message.error((r.json && r.json.detail) || "Could not update this setting.");
    }
  };

  return (
    <div className="leadset-row">
      <div className="leadset-row-label">
        Requests our team starts
        <div className="leadset-hint">
          When our FTC team checks a patient&rsquo;s insurance on its own &mdash; for example
          before an upcoming appointment &mdash; count it toward this office&rsquo;s usage (plan
          limits, overage and free-trial requests), like the office&rsquo;s own requests. The
          office&rsquo;s own requests always count.
        </div>
      </div>
      <div className="leadset-row-control">
        {failed ? (
          <span className="crm-sub">Couldn&rsquo;t load</span>
        ) : counts === null ? (
          <Spin size="small" />
        ) : (
          <>
            <Switch checked={counts} loading={saving} onChange={change} aria-label="Count requests our team starts" />
            <span>{counts ? "Counted" : "Not counted"}</span>
          </>
        )}
      </div>
    </div>
  );
}
